import React, { Fragment, useEffect, useState } from "react";
import { useHistory } from "react-router";
import { useTranslation } from "react-i18next";
import RouteManager, { PAGE_ROUTES } from "../../managers/RouteManager";
import AppManager from "../../managers/AppManager";
import DataGridHolderPayroll from "../common/DataGridHolderPayroll";
import { parseColumnConfig, parseVisibleColumnConfig, ShiftsColumnConfig } from "../configs/config";
import {
    showToast,
    TOAST_TYPE,
    showLoader,
    removeLoader,
    showAlert,
    DKLabel,
    Toggle
} from "deskera-ui-library";
import { deepClone, removeDelayedLoader, showDelayedLoader } from "../../utility/Utility";
import ShiftManagementService from "../../services/ShiftManagementService";
import CompanyDetailService from "../../services/CompanyDetailService";
import { TableManger, TABLES, TABLE_DISPLAY_NAME } from "../../managers/TableManger";
import { TENANT, TenantService } from "../../managers/TenantManagerService";
import TenantManager from "../../managers/TenantManager";
import { useDispatch, useSelector } from "react-redux";
import { fetchPaginatedShifts, selectPaginatedShifts } from "../../Redux/Slices/ShiftManagementSlice";
import { isProfessionalPlan, showUpgradePlanAlert } from "../../managers/common/CommonUtil";
import PermissionUtils, { ACTION, MODULE } from "../user-permissions/PermissionUtils";

function Shifts(props) {
    const history = useHistory();
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const [shiftData, setShiftData] = useState({
        columnData: [],
        rowData: [],
        filter: [],
        originalData: []
    });

    const shifts = useSelector(selectPaginatedShifts);
    const [enableShiftManagement, setEnableShiftManagement] = useState(TenantService.get(TENANT.IS_SHIFT_MANAGEMENT_ENABLED));
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPageCount, setTotalPageCount] = useState(0);
    const [reload, setReload] = useState(false);

    useEffect(() => {
        RouteManager.setPresenter({ props: { ...props, history: history } });
        AppManager.scrollToTop();
    }, []);

    useEffect(() => {
        if (enableShiftManagement) {
            getShifts(ShiftManagementService.getDefaultApiConfig());
        }

        updateCompanyDetail();
    }, [enableShiftManagement,reload]);

    useEffect(() => {
        if (shifts) {
            prepareGridData(shifts.data);
            setTotalPageCount(shifts.filterConfig.totalPage);
        }
    }, [shifts]);

    const getShifts = async (apiConfig) => {
        if (apiConfig) {
            ShiftManagementService.API_CONFIG = apiConfig;
        }

        const timeout = showDelayedLoader();
        await dispatch(fetchPaginatedShifts());
        removeDelayedLoader(timeout);
    }

    const updateCompanyDetail = () => {
        const tenantData = TenantManager.getPeopleTenantData();
        if (tenantData?.tenant?.enableShiftManagement !== enableShiftManagement) {
            showLoader(t("UPDATING_SETTING"));
            const payload = {...tenantData.tenant, enableShiftManagement: enableShiftManagement};
            CompanyDetailService.updateCompanyDetail(payload).then(
                (response) => {
                    removeLoader();
                    if (response && response['status'] === "OK") {
                        TenantManager.setPeopleTenantData(response);
                        TenantService.set(TENANT.IS_SHIFT_MANAGEMENT_ENABLED, enableShiftManagement);
                    }
                }, (error) => {
                    removeLoader();
                    showToast(t("INTERNAL_SERVER_ERROR"), TOAST_TYPE.FAILURE);
                }
            );
        }
    }

    const prepareGridData = (shifts) => {
        const columnConfig = parseVisibleColumnConfig(parseColumnConfig(TableManger.getTableVisibleColumns(TABLES.PEOPLE_SHIFTS), TABLES.PEOPLE_SHIFTS, {}));
        const shiftData = {
            columnData: deepClone(columnConfig),
            rowData: [],
            filter: [],
            originalData: []
        };

        if (shifts?.length > 0) {
            const rowData = ShiftsColumnConfig(shifts);
            shiftData.rowData = rowData;
            shiftData.originalData = rowData;

            setShiftData(shiftData);
        } else {
            setShiftData(shiftData);
        }
    }

    const onRowEdit = (data) => {
        if (!isProfessionalPlan()) {
            showUpgradePlanAlert('Shifts');
            return;
        }
        if (data?.rowData?.data) {
            ShiftManagementService.SHIFT = data.rowData.data;
            RouteManager.navigateToPage(PAGE_ROUTES.ADD_SHIFT);
        }
    }

    const showConfirmationMessage = (shiftIds) => {
        showAlert(t("ALERT"), t("DELETE_CONFIRM"), [
            {
                title: t("YES"),
                className: "bg-app text-white mr-xl",
                onClick: () => {
                    deleteShifts(shiftIds);
                }
            },
            {
                title: t("NO"),
                className: "bg-gray1 border-m"
            }
        ]);
    }

    const onRowDelete = (data) => {
        if (!isProfessionalPlan()) {
            showUpgradePlanAlert('Shifts');
            return;
        }
        if (data?.rowData?.id) {
            showConfirmationMessage([data.rowData.id]);
        }
    }

    const deleteShifts = (shiftIds) => {
        if (!isProfessionalPlan()) {
            showUpgradePlanAlert('Shifts');
            return;
        }
        showLoader(t("DELETE_SHIFT"));
        ShiftManagementService.deleteShift(shiftIds).then(
            (response) => {
                removeLoader();
                if (response.status === "OK") {
                    showToast(t("SHIFT_DELETE_SUCCESS"), TOAST_TYPE.SUCCESS);
                    getShifts();
                } else {
                    showToast(response?.message || t("INTERNAL_SERVER_ERROR"), TOAST_TYPE.FAILURE);
                }
            }, (error) => {
                removeLoader();
                showToast(error?.message || t("INTERNAL_SERVER_ERROR"), TOAST_TYPE.FAILURE);
            }
        );
    }

    return (
        <Fragment>
            <div className="row bg-white border-radius-m m-v-l p-v-l p-h-m">
                <div className="row">
                    <Toggle
                        className={`mr-s border-n ${PermissionUtils.hasAccess(MODULE.SHIFT,[ACTION.CREATE, ACTION.EDIT]) ? "":"disable-button"}`}
                        isOn={enableShiftManagement}
                        onChange={() => {
                                if(PermissionUtils.hasAccess(MODULE.SHIFT,[ACTION.CREATE, ACTION.EDIT])) {
                                    setEnableShiftManagement(!enableShiftManagement)
                                }
                                
                            }
                        }
                    />
                    <DKLabel text={t("ENABLE_SHIFT_MANAGEMENT")} />
                </div>
            </div>
            {enableShiftManagement &&
                <div>
                    <DataGridHolderPayroll
                        tableName={TABLES.PEOPLE_SHIFTS}
                        displayTableName={TABLE_DISPLAY_NAME[TABLES.PEOPLE_SHIFTS]}
                        allowBulkOperation={true}
                        allowColumnEdit={false}
                        allowColumnDelete={false}
                        allowColumnShift={false}
                        allowSearch={false}
                        allowFilter={false}
                        allowColumnAdd={false}
                        allowRowAdd={false}
                        allowColumnSort={false}
                        refresh={false}
                        allowDataExport={false}
                        allowRowEdit={PermissionUtils.hasAccess(MODULE.SHIFT,[ ACTION.EDIT])}
                        showContextEdit={false}
                        contextMenu={true}
                        isMenuVisible={false}
                        showContextMenu={true}
                        isActionEnabled={true}
                        gridData={shiftData}
                        currentPage={currentPage}
                        totalPageCount={totalPageCount}
                        onRowEdit={(data) => {
                            onRowEdit(data);
                        }}
                        onRowDelete={(data) => {
                            onRowDelete(data);
                        }}
                        onPaginationClick={(requestedPageNumber) => {
                            setCurrentPage(requestedPageNumber);

                            ShiftManagementService.API_CONFIG.currentPage = requestedPageNumber;
                            getShifts(ShiftManagementService.API_CONFIG);
                        }}
                        addButtonConfig={[{
                            title: `+ ${t("ADD_SHIFT")}`,
                            className: "bg-app text-white ml-s",
                            onClick: () => {
                                if (!isProfessionalPlan()) {
                                    showUpgradePlanAlert('Shifts');
                                    return;
                                } else {
                                    RouteManager.navigateToPage(PAGE_ROUTES.ADD_SHIFT);
                                }
                            }
                        }]}
                        addMenuButton={[
                            {
                                title: t("DELETE"),
                                className: "bg-red text-white border-red mr-r",
                                onClick: (data) => {
                                    if (data?.length > 0) {
                                        const shiftIds = data.map((s) => s.id);
                                        deleteShifts(shiftIds);
                                    }
                                }
                            }
                        ]}
                        onColumnShift={(data)=> setReload(!reload)}
                        module={MODULE.SHIFT}
                    />
                </div>
            }
        </Fragment>
    );
}

export default Shifts;
